import React, { useState } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, Loader } from 'lucide-react';
import { authAPI } from '../services/api';
import { setAuthToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(4px)'
  },
  modal: {
    backgroundColor: '#1A1A1A',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '440px',
    maxHeight: '90vh',
    overflow: 'auto',
    border: '1px solid #262626',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
  },
  header: {
    padding: '24px 24px 20px',
    borderBottom: '1px solid #262626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#F5F5F5',
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#A3A3A3',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  },
  content: {
    padding: '24px'
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    backgroundColor: '#262626',
    padding: '4px',
    borderRadius: '10px'
  },
  tab: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: '#A3A3A3',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  tabActive: {
    backgroundColor: '#8B5CF6',
    color: 'white'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#E5E5E5'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  icon: {
    position: 'absolute',
    left: '12px',
    color: '#A3A3A3'
  },
  input: {
    width: '100%',
    padding: '12px 12px 12px 40px',
    backgroundColor: '#262626',
    border: '1px solid #404040',
    borderRadius: '8px',
    color: '#F5F5F5',
    fontSize: '14px',
    transition: 'all 0.2s'
  },
  inputError: {
    borderColor: '#EF4444'
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    color: '#A3A3A3',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center'
  },
  error: {
    fontSize: '12px',
    color: '#EF4444',
    marginTop: '4px'
  },
  rememberMe: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer'
  },
  checkboxLabel: {
    fontSize: '13px',
    color: '#E5E5E5',
    cursor: 'pointer'
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#8B5CF6',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '24px 0',
    color: '#A3A3A3',
    fontSize: '13px'
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#404040'
  },
  googleButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'white',
    border: '1px solid #E5E5E5',
    borderRadius: '8px',
    color: '#262626',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px'
  },
  successMessage: {
    padding: '12px',
    backgroundColor: '#10B98120',
    border: '1px solid #10B981',
    borderRadius: '8px',
    color: '#10B981',
    fontSize: '13px',
    textAlign: 'center',
    marginBottom: '16px'
  }
};

const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  // ========================================
  // EMAIL/PASSWORD AUTH - DORMANT
  // To re-enable: change ENABLE_EMAIL_AUTH to true
  // ========================================
  const ENABLE_EMAIL_AUTH = false;

  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  if (!isOpen) return null;

  const handleClose = () => {
    setFormData({ email: '', username: '', password: '', confirmPassword: '' });
    setError('');
    setFieldErrors({});
    setSuccessMessage('');
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    setError('');
  };

  const validateForm = () => {
    const errors = {};

    if (mode === 'register') {
      if (!formData.username.trim()) {
        errors.username = 'Username is required';
      } else if (formData.username.length < 3) {
        errors.username = 'Username must be at least 3 characters';
      }
    }

    if (!formData.email.trim()) {
      errors.email = mode === 'login' ? 'Email or username is required' : 'Email is required';
    } else if (mode === 'register' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (mode === 'register' && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (mode === 'register' && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      if (mode === 'login') {
        // Login
        const response = await authAPI.login(formData.email, formData.password);

        if (response.success) {
          setAuthToken(response.token, rememberMe);
          setSuccessMessage('Login successful! Redirecting...');

          setTimeout(() => {
            if (onSuccess) onSuccess(response.user);
            handleClose();
          }, 1000);
        } else {
          setError(response.error || 'Login failed. Please try again.');
        }
      } else {
        // Register
        const response = await authAPI.register({
          email: formData.email,
          username: formData.username,
          password: formData.password,
          role: 'user'
        });

        if (response.success) {
          setAuthToken(response.token, rememberMe);
          setSuccessMessage('Account created successfully! Welcome!');

          setTimeout(() => {
            if (onSuccess) onSuccess(response.user);
            handleClose();
          }, 1000);
        } else {
          setError(response.error || 'Registration failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to Google OAuth endpoint
    window.location.href = `${API_URL}/auth/google`;
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Welcome to ReactorHub</h2>
          <button
            style={styles.closeButton}
            onClick={handleClose}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#262626';
              e.target.style.color = '#F5F5F5';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#A3A3A3';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* ========================================
              EMAIL/PASSWORD FORMS - DORMANT
              Only shown when ENABLE_EMAIL_AUTH = true
              ======================================== */}
          {ENABLE_EMAIL_AUTH && (
            <>
              {/* Tabs */}
              <div style={styles.tabs}>
                <button
                  style={{
                    ...styles.tab,
                    ...(mode === 'login' ? styles.tabActive : {})
                  }}
                  onClick={() => {
                    setMode('login');
                    setError('');
                    setFieldErrors({});
                  }}
                >
                  Login
                </button>
                <button
                  style={{
                    ...styles.tab,
                    ...(mode === 'register' ? styles.tabActive : {})
                  }}
                  onClick={() => {
                    setMode('register');
                    setError('');
                    setFieldErrors({});
                  }}
                >
                  Sign Up
                </button>
              </div>

              {/* Success Message */}
              {successMessage && (
                <div style={styles.successMessage}>
                  {successMessage}
                </div>
              )}

              {/* Form */}
              <form style={styles.form} onSubmit={handleSubmit}>
            {/* Username (Register only) */}
            {mode === 'register' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Username</label>
                <div style={styles.inputWrapper}>
                  <User size={18} style={styles.icon} />
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    style={{
                      ...styles.input,
                      ...(fieldErrors.username ? styles.inputError : {})
                    }}
                    placeholder="Choose a username"
                    disabled={loading}
                  />
                </div>
                {fieldErrors.username && (
                  <span style={styles.error}>{fieldErrors.username}</span>
                )}
              </div>
            )}

            {/* Email */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                {mode === 'login' ? 'Email or Username' : 'Email'}
              </label>
              <div style={styles.inputWrapper}>
                <Mail size={18} style={styles.icon} />
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    ...styles.input,
                    ...(fieldErrors.email ? styles.inputError : {})
                  }}
                  placeholder={mode === 'login' ? 'Enter email or username' : 'Enter your email'}
                  disabled={loading}
                />
              </div>
              {fieldErrors.email && (
                <span style={styles.error}>{fieldErrors.email}</span>
              )}
            </div>

            {/* Password */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.inputWrapper}>
                <Lock size={18} style={styles.icon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  style={{
                    ...styles.input,
                    paddingRight: '40px',
                    ...(fieldErrors.password ? styles.inputError : {})
                  }}
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  style={styles.eyeButton}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldErrors.password && (
                <span style={styles.error}>{fieldErrors.password}</span>
              )}
            </div>

            {/* Confirm Password (Register only) */}
            {mode === 'register' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Confirm Password</label>
                <div style={styles.inputWrapper}>
                  <Lock size={18} style={styles.icon} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    style={{
                      ...styles.input,
                      paddingRight: '40px',
                      ...(fieldErrors.confirmPassword ? styles.inputError : {})
                    }}
                    placeholder="Confirm your password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    style={styles.eyeButton}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <span style={styles.error}>{fieldErrors.confirmPassword}</span>
                )}
              </div>
            )}

            {/* Remember Me (Login only) */}
            {mode === 'login' && (
              <div style={styles.rememberMe}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={styles.checkbox}
                />
                <label htmlFor="rememberMe" style={styles.checkboxLabel}>
                  Remember me
                </label>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div style={{ ...styles.error, marginTop: 0, textAlign: 'center' }}>
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {})
              }}
              disabled={loading}
              onMouseEnter={(e) => {
                if (!loading) e.target.style.backgroundColor = '#7C3AED';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.target.style.backgroundColor = '#8B5CF6';
              }}
            >
              {loading ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  {mode === 'login' ? 'Logging in...' : 'Creating account...'}
                </>
              ) : (
                mode === 'login' ? 'Login' : 'Create Account'
              )}
            </button>
          </form>

          {/* Divider */}
          <div style={styles.divider}>
            <div style={styles.dividerLine} />
            <span>or</span>
            <div style={styles.dividerLine} />
          </div>
            </>
          )}

          {/* ========================================
              GOOGLE OAUTH - ALWAYS ACTIVE
              ======================================== */}

          {/* Welcome Message (shown when email auth is disabled) */}
          {!ENABLE_EMAIL_AUTH && (
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <p style={{ fontSize: '16px', color: '#E5E5E5', marginBottom: '8px' }}>
                Sign in to ReactorHub
              </p>
              <p style={{ fontSize: '13px', color: '#A3A3A3' }}>
                Vote for your favorite content and interact with the community
              </p>
            </div>
          )}

          {/* Google Login Button */}
          <button
            style={styles.googleButton}
            onClick={handleGoogleLogin}
            disabled={loading}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#F5F5F5';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
              <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
              <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
              <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
            </svg>
            Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
